import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { useAuth } from "../../../../context";
import useFetchObjects from "../../../../api/useFetchObjects";
import { useTranslation } from "react-i18next";

const DEFAULT_LAST_DAYS = 10;
const DEFAULT_LIMIT_USERS = 10;

// For export: pull large pages to reduce round trips (must be <= backend MAX_PAGE_SIZE)
const EXPORT_PAGE_SIZE = 500;

const AttendanceReport2 = () => {
  const { t } = useTranslation();
  const { user: userStorage } = useAuth();
  const token = userStorage?.token;

  const MonthOptions = [
    { id: "", value: t("hr.allMonths") },
    { id: 1, value: "January" },
    { id: 2, value: "February" },
    { id: 3, value: "March" },
    { id: 4, value: "April" },
    { id: 5, value: "May" },
    { id: 6, value: "June" },
    { id: 7, value: "July" },
    { id: 8, value: "August" },
    { id: 9, value: "September" },
    { id: 10, value: "October" },
    { id: 11, value: "November" },
    { id: 12, value: "December" },
  ];

  const currentYear = new Date().getFullYear();
  const startYear = 2023;
  const Years = [
    { id: "", value: t("hr.allYears") },
    ...Array.from({ length: currentYear - startYear + 1 }, (_, index) => ({
      id: startYear + index,
      value: startYear + index,
    })),
  ];

  // Filters (keep empty by default for fast subset)
  const [user, setUser] = useState("");
  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");
  const [startDate, setStartDate] = useState(""); // YYYY-MM-DD
  const [endDate, setEndDate] = useState(""); // YYYY-MM-DD
  const [forceFresh, setForceFresh] = useState(false);

  // Server pagination (controlled)
  const [page, setPage] = useState(0); // zero-based for DataGrid
  const [pageSize, setPageSize] = useState(50);
  const [rowCount, setRowCount] = useState(0);

  // Data
  const [userAttendanceData, setUserAttendanceData] = useState([]);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);

  // Export state
  const [exporting, setExporting] = useState(false);

  // Build query string for the main table request
  const queryString = useMemo(() => {
    const p = new URLSearchParams();

    if (user) p.append("user_id", user);
    if (year) p.append("year", year);
    if (month) p.append("month", month);
    if (startDate) p.append("start_date", startDate);
    if (endDate) p.append("end_date", endDate);

    // server pagination (API is 1-based)
    p.append("page", String(page + 1));
    p.append("page_size", String(pageSize));

    // fast default if no filters
    if (!user && !year && !month && !startDate && !endDate) {
      p.append("last_days", String(DEFAULT_LAST_DAYS));
      p.append("limit_users", String(DEFAULT_LIMIT_USERS));
    }
    if (forceFresh) p.append("fresh", "true");

    return p.toString();
  }, [user, year, month, startDate, endDate, page, pageSize, forceFresh]);

  // Users (unchanged)
  const { data: usersData, isLoading: isLoadingUsers } = useFetchObjects(
    ["attendance-users"],
    "attendance-users",
    token
  );

  useEffect(() => {
    if (usersData) {
      const formatted = Object.entries(usersData).map(([key, value]) => ({
        user_id: key,
        name: value,
      }));
      setUserAttendanceData(formatted);
    }
  }, [usersData]);

  // Records
  const {
    data: recordsData,
    refetch,
    isLoading: isLoadingRecords,
  } = useFetchObjects(
    ["attendance-records", queryString],
    `attendance-records/?${queryString}`,
    token
  );

  useEffect(() => {
    if (recordsData) {
      const list = recordsData.results || [];
      setRowCount(recordsData.total ?? list.length);
      setMeta(recordsData.meta || null);

      const mapped = list.map((record, index) => ({
        id: page * pageSize + index + 1, // stable id per page
        number: page * pageSize + index + 1,
        user: record.user_name,
        date: record.date,
        time: record.time,
        attendanceType: record.attendanceType,
        status: record.status,
      }));
      setRows(mapped);
    }
  }, [recordsData, page, pageSize]);

  // Helpers for actions
  const handleFilter = () => {
    setPage(0);
    setTimeout(() => refetch(), 0);
  };

  const handleReset = () => {
    setUser("");
    setYear("");
    setMonth("");
    setStartDate("");
    setEndDate("");
    setForceFresh(false);
    setPage(0);
    setPageSize(50);
    setTimeout(() => refetch(), 0);
  };

  const handleRefreshFromDevice = () => {
    setForceFresh(true);
    setTimeout(() => {
      refetch();
      setForceFresh(false);
    }, 0);
  };

  // Build base params for export (use current filters; no page/page_size here)
  const buildExportParams = () => {
    const p = new URLSearchParams();
    if (user) p.append("user_id", user);
    if (year) p.append("year", year);
    if (month) p.append("month", month);
    if (startDate) p.append("start_date", startDate);
    if (endDate) p.append("end_date", endDate);

    // If no filters, we’ll still export the fast subset (recent days & users).
    if (!user && !year && !month && !startDate && !endDate) {
      p.append("last_days", String(DEFAULT_LAST_DAYS));
      p.append("limit_users", String(DEFAULT_LIMIT_USERS));
    }
    return p;
  };

  // Fetch ALL pages for current filters for exporting
  const fetchAllFilteredRecords = async () => {
    setExporting(true);
    try {
      const baseParams = buildExportParams();
      let pageNum = 1;
      let total = Infinity;
      const all = [];

      while (all.length < total) {
        const params = new URLSearchParams(baseParams);
        params.append("page", String(pageNum));
        params.append("page_size", String(EXPORT_PAGE_SIZE));

        const resp = await fetch(`attendance-records/?${params.toString()}`, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        if (!resp.ok) throw new Error(`Export fetch failed (page ${pageNum})`);
        const json = await resp.json();
        const results = json.results || [];
        total = json.total ?? results.length;

        all.push(...results);
        if (results.length === 0) break; // safety
        pageNum += 1;
      }
      return all;
    } finally {
      setExporting(false);
    }
  };

  const exportToExcel = async () => {
    const all = await fetchAllFilteredRecords();

    const wb = XLSX.utils.book_new();
    const title = "Attendance Report";
    const headers = [
      ["#", "User", "Date", "Time", "Attendance Type", "Status"],
    ];

    const rowsAoA = all.map((r, idx) => [
      idx + 1,
      r.user_name,
      r.date,
      r.time,
      r.attendanceType,
      r.status,
    ]);

    const ws = XLSX.utils.aoa_to_sheet([[title], ...headers, ...rowsAoA]);
    ws["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: headers[0].length - 1 } },
    ];

    XLSX.utils.book_append_sheet(wb, ws, "Attendance Report");
    XLSX.writeFile(wb, "Attendance_Report.xlsx");
  };

  const exportToPDF = async () => {
    const all = await fetchAllFilteredRecords();

    const doc = new jsPDF();
    doc.autoTable({
      head: [["#", "User", "Date", "Time", "Attendance Type", "Status"]],
      body: all.map((r, idx) => [
        idx + 1,
        r.user_name,
        r.date,
        r.time,
        r.attendanceType,
        r.status,
      ]),
      startY: 20,
      styles: { fontSize: 9 },
    });
    doc.save("attendance.pdf");
  };

  // DataGrid columns
  const columns = [
    { field: "number", headerName: "#", width: 80 },
    { field: "user", headerName: t("hr.user"), flex: 1, minWidth: 160 },
    { field: "date", headerName: t("hr.date"), width: 140 },
    { field: "time", headerName: t("hr.time"), width: 140 },
    { field: "attendanceType", headerName: t("hr.attendanceType"), width: 160 },
    { field: "status", headerName: t("hr.status"), width: 140 },
  ];

  const showingFastSubset = !user && !year && !month && !startDate && !endDate;

  return (
    <Container>
      <Paper sx={{ padding: "1rem", marginTop: "2rem" }}>
        <Typography variant="h4" color="primary" fontWeight={500}>
          {t("hr.attendanceReport")}
        </Typography>

        <Grid container spacing={3} sx={{ marginTop: "1rem" }}>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              label={t("hr.selectUser")}
              fullWidth
              value={user}
              onChange={(e) => setUser(e.target.value)}
            >
              <MenuItem value="">{t("hr.allUsers")}</MenuItem>
              {userAttendanceData.map((item) => (
                <MenuItem key={item.user_id} value={item.user_id}>
                  {item.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              select
              label={t("hr.year")}
              fullWidth
              value={year}
              onChange={(e) => setYear(e.target.value)}
            >
              {Years.map((o) => (
                <MenuItem key={o.id} value={o.id}>
                  {o.value}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              select
              label={t("hr.month")}
              fullWidth
              value={month}
              onChange={(e) => setMonth(e.target.value)}
            >
              {MonthOptions.map((o) => (
                <MenuItem key={o.id} value={o.id}>
                  {o.value}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Optional date range */}
          <Grid item xs={12} sm={6}>
            <TextField
              type="date"
              fullWidth
              label={t("hr.startDate") || "Start date"}
              InputLabelProps={{ shrink: true }}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              type="date"
              fullWidth
              label={t("hr.endDate") || "End date"}
              InputLabelProps={{ shrink: true }}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </Grid>

          <Grid
            item
            xs={12}
            sm={12}
            display="flex"
            justifyContent="flex-end"
            gap={1}
          >
            <Button variant="outlined" onClick={handleReset}>
              {t("hr.reset") || "Reset"}
            </Button>
            <Button variant="outlined" onClick={handleRefreshFromDevice}>
              {t("hr.refreshFromDevice") || "Force refresh"}
            </Button>
            <Button variant="contained" color="primary" onClick={handleFilter}>
              {t("hr.filter")}
            </Button>
          </Grid>
        </Grid>

        {showingFastSubset && (
          <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
            {t("hr.showingRecentSubset") ||
              `Showing the last ${DEFAULT_LAST_DAYS} days for up to ${DEFAULT_LIMIT_USERS} most recent users.`}
          </Typography>
        )}

        {/* Keep table visible; show slim progress while fetching */}
        {isLoadingUsers && (
          <Box display="flex" justifyContent="center" mt={2}>
            <CircularProgress />
          </Box>
        )}
        {isLoadingRecords && <LinearProgress sx={{ mt: 2 }} />}

        {/* Meta info from server cache */}
        {meta && (
          <Typography
            variant="caption"
            sx={{ display: "block", mt: 1 }}
            color="text.secondary"
          >
            Last synced: {meta.generated_at || "n/a"}{" "}
            {meta.source ? `(${meta.source})` : ""}
          </Typography>
        )}

        <Box sx={{ height: 600, width: "100%", mt: 2 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            density="compact"
            disableRowSelectionOnClick
            loading={isLoadingRecords}
            // --- FIX: fully controlled server pagination ---
            paginationMode="server"
            rowCount={rowCount}
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={({ page: p, pageSize: ps }) => {
              setPage(p);
              setPageSize(ps);
              setTimeout(() => refetch(), 0);
            }}
            pageSizeOptions={[25, 50, 100, 200]}
            getRowId={(row) => row.id}
          />
        </Box>

        <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
          <Button
            onClick={exportToExcel}
            variant="contained"
            disabled={exporting}
          >
            {exporting
              ? t("hr.exporting") || "Exporting…"
              : t("hr.exportToExcel")}
          </Button>
          <Button
            onClick={exportToPDF}
            variant="contained"
            color="secondary"
            disabled={exporting}
          >
            {exporting
              ? t("hr.exporting") || "Exporting…"
              : t("hr.exportToPDF")}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default AttendanceReport2;
